import React, { useState } from 'react';
import {
  Box,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
// import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { AppColors, typographyClamps } from '../utils/utils';
import useToggle from '../hooks/useToggle';
import Page from '../components/Page';
import ImagePreviewDialog from '../components/models/ImagePreviewDialog';
import VideoModel from '../components/models/VideoModel';
import PathsSection from '../components/Pathssection';

const webFeatures = [
  { icon: '⚡', text: 'Real Conversations — Not Scripts' },
  { icon: '⏱', text: '24/7 Instant Response' },
  { icon: '🎯', text: 'Live Lead Qualification' },
  { icon: '🔄', text: 'Automatic Routing & Alerts' },
  { icon: '🧠', text: 'Inventory-Aware Intelligence' },
  { icon: '📈', text: 'More Appointments. More Sales.' },
];

const featureCards: string[] = [
  '/static/web_page/img_web_miles_1.png',
  '/static/web_page/img_web_miles_2.png',
  '/static/web_page/img_web_miles_3.png',
  '/static/web_page/img_web_miles_4.png',
  '/static/web_page/img_web_miles_5.png',
  '/static/web_page/img_web_miles_6.png',
  '/static/web_page/img_web_miles_7.png',
  '/static/web_page/img_web_miles_8.png',
  '/static/web_page/img_web_miles_9.png',
  '/static/web_page/img_web_miles_10.png',
  '/static/web_page/img_web_miles_11.png',
  '/static/web_page/img_web_miles_12.png',
  '/static/web_page/img_web_miles_13.png',
  '/static/web_page/img_web_miles_14.png',
  '/static/web_page/img_web_miles_15.png',
  '/static/web_page/img_web_miles_16.png',
];

const Web: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // e.g. <600px

  // const xMargin = { xs: 2, sm: 2, md: '8%', lg: '10%' };
  const [videoUrl, setVideoUrl] = useState<string | undefined>();
  const { toggle, onOpen, onClose } = useToggle();

  const [previewImagePath, setPreviewImagePath] = useState<
    string | undefined
  >();

  const {
    toggle: toggleImagePreviewModel,
    onOpen: onOpenImagePreviewModel,
    onClose: onCloseImagePreviewModel,
  } = useToggle();

  const openImageViewView = (imagePath: string | undefined) => {
    if (imagePath !== undefined) {
      setPreviewImagePath(imagePath);
      onOpenImagePreviewModel();
    }
  };

  const handlePlayVideo = (videoPath: string) => {
    setVideoUrl(videoPath);
    onOpen();
  };

  const getAiPoweredView = () => (
    <Box
      display={'flex'}
      flexDirection={'column'}
      justifyContent={'center'}
      textAlign={'center'}
      // mt={2}
      fontSize={'clamp(1rem, 2vw, 2rem)'}
      py={{ xs: 1, sm: 0 }}
      position={'relative'}
    >
      <Typography
        sx={{
          fontSize: {
            xs: typographyClamps.h5,
            sm: typographyClamps.h4,
            md: typographyClamps.h4,
            lg: typographyClamps.h3,
          },
        }}
      >
        This isn’t a chatbot. It’s a Sales Agent
      </Typography>

      <Typography
        fontSize={'inherit'}
        fontWeight={600}
        sx={{
          mt: 2,
          '& span': {
            // color: { xs: 'inherit', sm: AppColors.vividAzureColor },
            color: AppColors.vividAzureColor,
          },
        }}
      >
        <span>Miles</span> <strong>replaces dead-end chatbots</strong>{' '}
        <span>
          with a real AI Agent that guides buyers, qualifies intent, and routes
          live leads to your sales team.
        </span>
      </Typography>
    </Box>
  );

  return (
    <>
      <Page title={'The Web'}>
        <Grid
          container
          sx={{
            display: 'flex',
            flexDirection: 'row',
            py: 4,
            px: { xs: '5%', sm: '10%' },
            backgroundImage: `url('/static/web_page/img_web_banner.png')`,
            backgroundPosition: 'center',
            backgroundSize: isMobile ? 'cover' : '100% 100%',
            backgroundRepeat: 'no-repeat',
            color: 'white',
            // gap: 2,
          }}
        >
          <Grid
            size={{ xs: 12, sm: 9 }}
            display='flex'
            flexDirection='column'
            justifyContent={{ xs: 'center', sm: 'start' }}
            alignItems='center'
            minHeight={{ xs: 'auto', sm: '40vh', md: '50vh' }} // important to fill container height
            fontSize={'clamp(1.2rem, 2vw, 2rem)'}
          >
            {isMobile ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  textAlign: 'center',
                  justifyContent: 'center',
                }}
              >
                {getAiPoweredView()}
              </Box>
            ) : (
              getAiPoweredView()
            )}

            <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
              <Box
                component={'img'}
                src='/static/lot_page/img_lot_miles_ai_agent_phone.png'
                sx={{
                  // width: { xs: '70%', sm: '100%', md: '80%', lg: '70%' },
                  height: '100%',
                  maxHeight: '45vh',
                  p: 2,
                  bgcolor: '#1961FF24',
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  openImageViewView((e.currentTarget as HTMLImageElement).src);
                }}
              />
            </Box>
          </Grid>

          <Grid
            size={{ xs: 5, sm: 3 }}
            display={{ xs: 'none', sm: 'flex' }}
            flex={1}
          >
            <Box
              component={'img'}
              src='/static/lot_page/img_lot_miles_ai_agent_phone.png'
              sx={{
                width: '100%',
                height: 'auto',
                mx: 'auto',
                p: 4,
                bgcolor: '#1961FF24',
                borderRadius: 4,
                cursor: 'pointer',
              }}
              onClick={(e) => {
                openImageViewView((e.currentTarget as HTMLImageElement).src);
              }}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            m: { xs: 2, sm: 4 },
            py: 4,
            px: { xs: '5%', sm: '10%', md: 0 },
            bgcolor: AppColors.lightBlueColor,
            borderRadius: 4,
          }}
        >
          <Typography
            sx={{
              fontSize: typographyClamps.h4,
              fontWeight: 'bold',
            }}
          >
            Your Website Is Your First Showroom
          </Typography>

          <Typography
            sx={{
              fontSize: typographyClamps.h6,
              my: 1,
            }}
          >
            Miles transforms website traffic into guided conversations, verified
            leads, and real appointments — automatically.
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr', // mobile
                sm: 'repeat(2, 1fr)', // tablet & desktop
              },
              columnGap: { sm: 6 },
              rowGap: 2,
              mt: 2,
            }}
          >
            {webFeatures.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Typography fontSize={{ xs: '18px', sm: '20px' }}>
                  {item.icon}
                </Typography>
                <Typography
                  fontSize={{ xs: '18px', sm: '20px' }}
                  fontWeight={500}
                >
                  {item.text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* <Box
          component='img'
          src='/static/web_page/img_web_rail.png'
          alt='Website Screenshot'
          sx={{
            width: 'auto', // responsive scaling
            height: 'auto', // keep aspect ratio
            maxWidth: { xs: '90%', sm: '80%', md: 1200 }, // optional: limit large screens
            display: 'block',
            mx: 'auto', // center horizontally
            px: { xs: '5%', sm: '10%', md: 0 },
            mt: { xs: 1, sm: 2 },
          }}
        /> */}

        <Grid
          container
          sx={{
            py: 4,
            px: '10%',
            mt: 4,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            // background: AppColors.aiGradient8,
          }}
        >
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography
              sx={{
                fontSize: typographyClamps.h4,
                // fontWeight: 'bold',
              }}
            >
              QR Buddy Web Rail
            </Typography>
            <Typography
              sx={{
                fontSize: typographyClamps.h6,
                fontWeight: 400,
                mt: { xs: 0, sm: 2 },
              }}
            >
              The QR Buddy web rail integrates seamlessly with your existing
              interface, offering online shoppers immediate access to essential
              information. Powered by the Miles AI agent, the platform provides
              real-time inquiry resolution and strategic lead capture,
              transforming casual browsers into high-intent prospects.
            </Typography>

            <List dense sx={{ pl: 1, mt: { xs: 1, sm: 2 } }}>
              {[
                'Search by new, used, make, model, price',
                'Direct connection to your current inventory',
                'Auto Spotlight provides quick access to vehicle data',
                'Similar Vehicles search expands viewers’ options',
                'Shareable link to Auto Spotlight ',
                'Request and schedule test drive',
                'Pricing, finance options, and more',
                'Virtual walkthrough request',
                'Miles AI Agent turns browsers into leads ',
              ].map((item, lIdx) => (
                <ListItem
                  key={lIdx}
                  sx={{
                    px: 0,
                    py: 0.25,
                    display: 'flex',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box
                    component='span'
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: 'text.primary',
                      mt: 1,
                      mr: 1.5,
                      flexShrink: 0,
                    }}
                  />
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{
                      variant: 'body2',
                      sx: {
                        // fontSize: { xs: '0.85rem', sm: '0.9rem' },
                        fontSize: typographyClamps.h6,
                        lineHeight: 1.2,
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid
            size={{ xs: 12, sm: 5.5 }}
            textAlign={{ xs: 'center', sm: 'center' }}
          >
            <Box
              component='img'
              src='/static/web_page/img_web_rail.png'
              alt='Website Screenshot'
              sx={{
                width: 'auto', // responsive scaling
                height: 'auto', // keep aspect ratio
                maxWidth: { xs: '90%', sm: '100%', md: 1200 }, // optional: limit large screens
                maxHeight: { xs: '90%', sm: '100%', md: '80vh', lg: '70vh' }, // optional: limit large screens
                display: 'block',
                mx: 'auto', // center horizontally
                // px: { xs: '5%', sm: '10%', md: 0 },
                // mt: { xs: 1, sm: 2 },
                cursor: 'pointer',
              }}
              onClick={(e) => {
                openImageViewView((e.currentTarget as HTMLImageElement).src);
              }}
            />
          </Grid>
        </Grid>

        <Grid
          container
          sx={{
            py: 4,
            px: '10%',
            mt: 4,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <Grid size={{ xs: 12, sm: 9 }}>
            <Stack
              sx={{
                textAlign: 'center',
                position: 'relative',
              }}
            >
              <Typography
                sx={{
                  fontSize: typographyClamps.h4,
                  fontWeight: 400,
                  whiteSpace: 'pre-line',
                }}
              >
                {'Two Intelligent Entry Points.\nOne Lead Engine.'}
              </Typography>
              <Typography
                sx={{
                  fontSize: typographyClamps.h5,
                  fontWeight: 400,
                  textAlign: 'start',
                }}
              >
                Seamless lead generation from your lot to your website. All
                roads lead to Miles AI.
              </Typography>
            </Stack>
          </Grid>
          <Grid
            size={{ xs: 12, sm: 1.5 }}
            textAlign={{ xs: 'center', sm: 'end' }}
          >
            <Box
              component={'img'}
              src='/static/web_page/ic_web_entry_points.png'
              sx={{
                maxWidth: { xs: '40%', sm: '120%' },
              }}
            />
          </Grid>
        </Grid>

        <PathsSection
          onPlayVideo={handlePlayVideo}
          onOpenImage={openImageViewView}
        />

        <Stack
          sx={{
            textAlign: 'center',
            position: 'relative',
            mt: { xs: 2, sm: 4 },
          }}
        >
          <Typography
            sx={{
              fontSize: typographyClamps.h4,
              fontWeight: 400,
            }}
          >
            Real Questions. Real Intelligence. Real Answers.
          </Typography>
          <Typography
            sx={{
              fontSize: typographyClamps.h5,
              fontWeight: 400,
            }}
          >
            See how Miles handles real buyer questions — and why basic bots fall
            behind.
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            p: { xs: 1, sm: 2 },
            position: 'relative',
          }}
        >
          <Grid container gap={{ xs: 1, sm: 2 }} justifyContent={'center'}>
            {featureCards.map((item, idx) => {
              return (
                <Grid
                  size={{ xs: 5.7, sm: 5.8, md: 2.8 }}
                  key={`screenshot-item-${idx + 1}`}
                  display={'flex'}
                  justifyContent={'center'}
                >
                  <Box
                    component={'img'}
                    src={item}
                    sx={{
                      width: '100%',
                      maxWidth: '300px',
                      borderRadius: 2,
                      cursor: 'pointer',
                    }}
                    onClick={() => openImageViewView(item)}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Page>

      {videoUrl && (
        <VideoModel
          onClose={onClose}
          toggle={toggle}
          videoUrl={videoUrl}
          title=''
        />
      )}

      <ImagePreviewDialog
        open={toggleImagePreviewModel}
        onClose={onCloseImagePreviewModel}
        imageSrc={previewImagePath || ''}
        imageAlt='Image'
        title='Image Preview'
      />
    </>
  );
};

export default Web;
